// eslint-disable-next-line no-restricted-imports
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { AxiosResponse } from 'axios';
import validator from 'validator';

import { API } from 'core/api';
import { useCognitoUser } from 'core/cognito';
import { useToast } from 'core/toast/hooks';

// The invoicing increment (FIGO-4230) returns bare payloads (no { result, meta }
// envelope). We still normalise defensively so a future envelope wouldn't break us.
function unwrap<T>(data: unknown): T {
  if (data && typeof data === 'object' && !Array.isArray(data) && 'result' in data) {
    return (data as { result: T }).result;
  }
  return data as T;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface InvoicingContract {
  organisation_id: string;
  invoicing_active: boolean;
  contract_start: string | null;
  billing_frequency_months: number;
  // null = open-ended ("until cancelled"); N = fixed term of N billing cycles.
  contract_term_cycles: number | null;
  // Derived by the backend from contract_start + term; null when open-ended.
  contract_end: string | null;
  // Derived: true iff billing_frequency_months > 1.
  subscription_billed_in_advance: boolean;
  usage_frequency_months: number;
  commission_frequency_months: number;
  subscription_name: string | null;
  subscription_net_price: number;
  discount_net: number;
  vat_id: string | null;
}

// Partial upsert: only send the fields the section owns. contract_end and
// subscription_billed_in_advance are derived and must never be sent.
interface InvoicingContractBody {
  invoicing_active?: boolean;
  contract_start?: string | null;
  billing_frequency_months?: number;
  contract_term_cycles?: number | null;
  usage_frequency_months?: number;
  commission_frequency_months?: number;
  subscription_name?: string | null;
  subscription_net_price?: number;
  discount_net?: number;
  vat_id?: string | null;
}

type InvoiceLineCategory = 'subscription' | 'discount' | 'usage' | 'one_off' | 'passthrough';

interface InvoiceLine {
  category: InvoiceLineCategory;
  description: string;
  period_start: string | null;
  period_end: string | null;
  amount_net: number; // signed cents
  // Gross is only served by newer API versions; the dialog leaves the incl.
  // VAT column out until it is there, rather than deriving it from a rate the
  // line does not carry.
  amount_gross?: number; // signed cents
  counts_as_revenue: boolean;
  item_id: string | null;
}

type InvoiceStatus = 'invoiced' | 'draft' | 'scheduled';

interface InvoiceBlock {
  // 'invoiced' = fact, generated and pushed to Exact; 'draft' = generated but
  // never accepted by Exact (a dry run, or a failed send), so it can still
  // change; 'scheduled' = projection from the saved settings.
  status: InvoiceStatus;
  invoice_date: string;
  period_start: string | null;
  period_end: string | null;
  // Exact invoice reference; null on scheduled rows.
  reference: string | null;
  lines: InvoiceLine[];
  subscription_net: number; // subscription + discount lines
  usage_net: number;
  other_net: number; // one_off + passthrough lines
  net_total: number;
  // See `amount_gross` on the line: absent until the API serves it.
  gross_total?: number;
}

interface OrganisationInvoicesResult {
  organisation_id: string;
  // Combined, oldest first: invoiced facts, then scheduled projections.
  invoices: InvoiceBlock[];
}

interface NextInvoiceUsage {
  fee_type: string;
  count: number;
  fee_amount: number;
  starting_from: number;
  billable_count: number;
  total_cents: number;
}

interface NextInvoice {
  organisation_id: string;
  invoicing_active: boolean;
  contract_start: string | null;
  billing_frequency_months: number;
  contract_term_cycles: number | null;
  contract_end: string | null;
  subscription_billed_in_advance: boolean;
  usage_frequency_months: number;
  commission_frequency_months: number;
  next_invoice_date: string | null;
  next_invoice: InvoiceBlock | null;
  // Projected invoices of a month before this one that were never generated
  // (backdated contract start).
  earlier_invoice_count: number;
  // This month onward, ascending, without the months that already have an
  // invoice; capped at 12 for open-ended contracts.
  scheduled_invoices: InvoiceBlock[];
  usage_breakdown: NextInvoiceUsage[];
}

interface ScheduledItem {
  id: string;
  organisation_id: string;
  description: string;
  amount_net: number; // signed: negative = credit
  invoice_on: string;
  vat_id: string | null;
  counts_as_revenue: boolean; // false = pass-through cost
  invoice_id: string | null;
  processed_on: string | null;
}

interface ScheduledItemBody {
  description: string;
  amount_net: number; // signed
  invoice_on: string;
  vat_id?: string | null;
  counts_as_revenue?: boolean;
}

interface UseInvoicingProps {
  id: string;
}

// ---------------------------------------------------------------------------
// Billing contract
// ---------------------------------------------------------------------------

function useInvoicingContract({ id }: UseInvoicingProps) {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  function fetchData() {
    return API.get<InvoicingContract | null, undefined, false>({
      path: `/admin/organisation/${id}/invoicing`,
      token,
      params: undefined,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<InvoicingContract | null>, unknown, InvoicingContract | null>(
    ['invoicing-contract', { id }],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token && validator.isUUID(id),
      retry: 0,
      select: ({ data }) => (!data ? null : unwrap<InvoicingContract>(data)),
    },
  );
}

function useUpdateInvoicingContract() {
  const queryClient = useQueryClient();
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface UpdateInvoicingContractProps {
    id: string;
    contract: InvoicingContractBody;
  }

  async function updateInvoicingContract({ id, contract }: UpdateInvoicingContractProps) {
    const { data } = await API.put<void, InvoicingContractBody>({
      path: `/admin/organisation/${id}/invoicing`,
      token,
      version: 1,
      body: contract,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });

    return Promise.resolve(data);
  }

  return useMutation<void, unknown, UpdateInvoicingContractProps>(
    (props: UpdateInvoicingContractProps) => updateInvoicingContract(props),
    {
      onSuccess: async (_, { id }) => {
        await queryClient.invalidateQueries(['invoicing-contract', { id }]);
        await queryClient.invalidateQueries(['next-invoice', { id }]);
        await queryClient.invalidateQueries(['invoices', { id }]);
        await queryClient.invalidateQueries(['organisations', { id }]);
      },
    },
  );
}

// ---------------------------------------------------------------------------
// Next invoice preview + projected schedule
// ---------------------------------------------------------------------------

function useNextInvoice({ id }: UseInvoicingProps) {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  function fetchData() {
    return API.get<NextInvoice, undefined, false>({
      path: `/admin/organisation/${id}/next-invoice`,
      token,
      params: undefined,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<NextInvoice>, unknown, NextInvoice>(
    ['next-invoice', { id }],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token && validator.isUUID(id),
      retry: 0,
      select: ({ data }) => unwrap<NextInvoice>(data),
    },
  );
}

// ---------------------------------------------------------------------------
// Combined invoices: invoiced facts (Exact) + scheduled projections
// ---------------------------------------------------------------------------

function useOrganisationInvoices({ id }: UseInvoicingProps) {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  function fetchData() {
    return API.get<OrganisationInvoicesResult, { show_scheduled: boolean }, false>({
      path: `/admin/organisation/${id}/invoices`,
      token,
      params: { show_scheduled: true },
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<OrganisationInvoicesResult>, unknown, OrganisationInvoicesResult>(
    ['invoices', { id }],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token && validator.isUUID(id),
      retry: 0,
      select: ({ data }) => unwrap<OrganisationInvoicesResult>(data),
    },
  );
}

// ---------------------------------------------------------------------------
// Scheduled items (one-time fees; negative = credit, counts_as_revenue false =
// pass-through cost)
// ---------------------------------------------------------------------------

function useScheduledItems({ id }: UseInvoicingProps) {
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  function fetchData() {
    return API.get<ScheduledItem[], undefined, false>({
      path: `/admin/organisation/${id}/scheduled-items`,
      token,
      params: undefined,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });
  }

  return useQuery<AxiosResponse<ScheduledItem[]>, unknown, ScheduledItem[]>(
    ['scheduled-items', { id }],
    fetchData,
    {
      staleTime: Number.MAX_VALUE,
      enabled: !!token && validator.isUUID(id),
      retry: 0,
      select: ({ data }) => (Array.isArray(data) ? data : unwrap<ScheduledItem[]>(data) ?? []),
    },
  );
}

function useCreateScheduledItem() {
  const queryClient = useQueryClient();
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface CreateScheduledItemProps {
    id: string;
    item: ScheduledItemBody;
  }

  async function createScheduledItem({ id, item }: CreateScheduledItemProps) {
    const { data } = await API.post<ScheduledItem, ScheduledItemBody>({
      path: `/admin/organisation/${id}/scheduled-items`,
      token,
      version: 1,
      body: item,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });

    return Promise.resolve(data);
  }

  return useMutation<ScheduledItem, unknown, CreateScheduledItemProps>(
    (props: CreateScheduledItemProps) => createScheduledItem(props),
    {
      onSuccess: async (_, { id }) => {
        await queryClient.invalidateQueries(['scheduled-items', { id }]);
        await queryClient.invalidateQueries(['next-invoice', { id }]);
        await queryClient.invalidateQueries(['invoices', { id }]);
      },
    },
  );
}

function useUpdateScheduledItem() {
  const queryClient = useQueryClient();
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface UpdateScheduledItemProps {
    id: string;
    itemId: string;
    item: Partial<ScheduledItemBody>;
  }

  async function updateScheduledItem({ id, itemId, item }: UpdateScheduledItemProps) {
    const { data } = await API.put<ScheduledItem, Partial<ScheduledItemBody>>({
      path: `/admin/organisation/${id}/scheduled-items/${itemId}`,
      token,
      version: 1,
      body: item,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });

    return Promise.resolve(data);
  }

  return useMutation<ScheduledItem, unknown, UpdateScheduledItemProps>(
    (props: UpdateScheduledItemProps) => updateScheduledItem(props),
    {
      onSuccess: async (_, { id }) => {
        await queryClient.invalidateQueries(['scheduled-items', { id }]);
        await queryClient.invalidateQueries(['next-invoice', { id }]);
        await queryClient.invalidateQueries(['invoices', { id }]);
      },
    },
  );
}

function useDeleteScheduledItem() {
  const queryClient = useQueryClient();
  const { token } = useCognitoUser();
  const { addToast } = useToast();

  interface DeleteScheduledItemProps {
    id: string;
    itemId: string;
  }

  async function deleteScheduledItem({ id, itemId }: DeleteScheduledItemProps) {
    const { data } = await API.delete<void>({
      path: `/admin/organisation/${id}/scheduled-items/${itemId}`,
      token,
      version: 1,
      onError: (error) => {
        addToast({ error, severity: 'error' });
      },
    });

    return Promise.resolve(data);
  }

  return useMutation<void, unknown, DeleteScheduledItemProps>(
    (props: DeleteScheduledItemProps) => deleteScheduledItem(props),
    {
      onSuccess: async (_, { id }) => {
        await queryClient.invalidateQueries(['scheduled-items', { id }]);
        await queryClient.invalidateQueries(['next-invoice', { id }]);
        await queryClient.invalidateQueries(['invoices', { id }]);
      },
    },
  );
}

export {
  type InvoiceBlock,
  type InvoiceLine,
  type InvoiceLineCategory,
  type InvoiceStatus,
  type InvoicingContract,
  type InvoicingContractBody,
  type NextInvoice,
  type NextInvoiceUsage,
  type OrganisationInvoicesResult,
  type ScheduledItem,
  type ScheduledItemBody,
  useCreateScheduledItem,
  useDeleteScheduledItem,
  useInvoicingContract,
  useNextInvoice,
  useOrganisationInvoices,
  useScheduledItems,
  useUpdateInvoicingContract,
  useUpdateScheduledItem,
};
